import React, { useState, useEffect, useRef, useId } from 'react';
import { Button } from './Button';
import { warmupAudio } from '../services/sound';
import { AppSettings } from '../types';

interface TimerProps {
  initialMinutes?: number;
  onIntervalComplete?: (duration: number) => void;
  isDemo?: boolean;
  size?: 'default' | 'compact';
  settings?: AppSettings;
  restartSignal?: number;
  alarmActive?: boolean;
  soundEnabled?: boolean;
}

export const Timer: React.FC<TimerProps> = ({ 
  initialMinutes = 20, 
  onIntervalComplete, 
  isDemo = false,
  size = 'default',
  settings: settingsProp,
  restartSignal,
  alarmActive = false,
  soundEnabled = true
}) => {
  const [duration, setDuration] = useState(settingsProp?.defaultInterval ?? initialMinutes);
  const [timeLeft, setTimeLeft] = useState((settingsProp?.defaultInterval ?? initialMinutes) * 60 * 1000);
  const [status, setStatus] = useState<'idle' | 'running' | 'paused'>('idle');
  const [targetTime, setTargetTime] = useState<number | null>(null);
  const [userAdjusted, setUserAdjusted] = useState(false);
  const rangeId = useId();
  

  const timerRef = useRef<number | null>(null);

  const formatTime = (ms: number) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    if (status === 'idle') {
      const now = Date.now();
      const target = now + (duration * 60 * 1000);
      setTargetTime(target);
      setTimeLeft(duration * 60 * 1000);
      setStatus('running');
      
      // Unlock audio context on user interaction
      warmupAudio();
    } else if (status === 'paused') {
      const now = Date.now();
      const target = now + timeLeft;
      setTargetTime(target);
      setStatus('running');
    }
  };

  const handlePause = () => {
    if (status === 'running') {
      setStatus('paused');
      if (timerRef.current) clearInterval(timerRef.current);
      // timeLeft is already updated by the interval, but to be safe:
      if (targetTime) {
          setTimeLeft(Math.max(0, targetTime - Date.now()));
      }
      setTargetTime(null);
    }
  };

  const handleReset = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setStatus('idle');
    setTimeLeft(duration * 60 * 1000);
    setTargetTime(null);
  };


  useEffect(() => {
    if (status === 'running' && targetTime) {
      timerRef.current = window.setInterval(() => {
        const now = Date.now();
        const remaining = targetTime - now;

        if (remaining <= 0) {
          // Timer finished
          if (timerRef.current) clearInterval(timerRef.current);
          setTimeLeft(0);
          setStatus('idle');
          if (onIntervalComplete) {
            onIntervalComplete(duration);
          }
          // Auto reset for UI
          setTimeout(() => {
             setTimeLeft(duration * 60 * 1000);
          }, 1000);
        } else {
          setTimeLeft(remaining);
        }
      }, 200); // Check frequently for UI smoothness
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status, targetTime, duration, onIntervalComplete]);

  // Update timeLeft when duration settings change in idle
  useEffect(() => {
      if (status === 'idle') {
          setTimeLeft(duration * 60 * 1000);
      }
  }, [duration, status]);

  useEffect(() => {
    if (!settingsProp) {
      return;
    }
    if (!userAdjusted && status === 'idle' && settingsProp.defaultInterval !== duration) {
      setDuration(settingsProp.defaultInterval);
      setTimeLeft(settingsProp.defaultInterval * 60 * 1000);
    }
  }, [settingsProp, status, duration, userAdjusted]);

  const didMount = useRef(false);

  useEffect(() => {
    if (restartSignal === undefined) {
      return;
    }
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (status === 'idle') {
      handleStart();
    }
  }, [restartSignal]);

  const isCompact = size === 'compact';
  const containerClasses = isCompact
    ? 'p-4 sm:p-5 max-w-sm'
    : 'p-6 sm:p-8 max-w-md';
  const timeClasses = isCompact
    ? 'text-5xl sm:text-6xl'
    : 'text-7xl';
  const sectionGap = isCompact ? 'mb-6' : 'mb-8';
  const settingsPadding = isCompact ? 'pt-4' : 'pt-6';
  const buttonSize = isCompact ? 'md' : 'lg';

  return (
    <div
      className={`bg-surface rounded-xl shadow-lg border w-full mx-auto ${containerClasses} ${
        alarmActive ? 'border-primary animate-pulse' : 'border-border'
      }`}
    >
      {/* Header / Display */}
      <div className={`text-center ${sectionGap}`}>
        <div className={`${timeClasses} font-bold text-text tabular-nums tracking-tight mb-2`}>
          {formatTime(timeLeft)}
        </div>
        <p className="text-muted text-sm font-medium uppercase tracking-widest">
            {status === 'idle' ? 'Ready to focus' : status === 'paused' ? 'Paused' : 'Focusing...'}
        </p>
        {alarmActive && !soundEnabled && (
          <p className="text-xs text-no mt-2">Sound is off, check in now.</p>
        )}
      </div>

      {/* Controls */}
      <div className={`flex flex-col gap-4 ${sectionGap}`}>
        {status === 'running' ? (
          <Button onClick={handlePause} variant="secondary" size={buttonSize} fullWidth>
            Pause
          </Button>
        ) : (
          <Button onClick={handleStart} variant="primary" size={buttonSize} fullWidth>
            {status === 'paused' ? 'Resume' : 'Start Focus'}
          </Button>
        )}
        
        {(status === 'paused' || status === 'running') && (
           <Button onClick={handleReset} variant="ghost" size="sm" className="text-muted hover:text-no">
             Reset Timer
           </Button>
        )}
      </div>

      {/* Settings */}
      <div className={`border-t border-border ${settingsPadding}`}>
        <div className="flex flex-col gap-4">
            <div className={`transition-opacity ${status !== 'idle' ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex justify-between items-center mb-2">
                    <label htmlFor={rangeId} className="text-sm font-medium text-text">Interval Duration</label>
                    <span className="text-sm font-bold text-primary">{duration} min</span>
                </div>
                <input 
                    id={rangeId}
                    type="range" 
                    min="5" 
                    max="120" 
                    step="5"
                    value={duration} 
                    onChange={(e) => {
                      const nextDuration = Number(e.target.value);
                      setDuration(nextDuration);
                      setUserAdjusted(true);
                    }}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />
            </div>
        </div>
      </div>
      
      {isDemo && (
          <div className="mt-4 pt-4 border-t border-dashed border-border text-center">
              <p className="text-xs text-muted">⚠️ Demo mode: Data is not saved.</p>
          </div>
      )}
    </div>
  );
};
